import React, { useState, useEffect, useMemo } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Building2, Trophy, Trash2, Users as UsersIcon } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import SearchableAthleteSelect from './SearchableAthleteSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const blankParticipant = (athlete_id = '') => ({
  athlete_id,
  minutes_played: 90,
  position: '',
  goals_scored: 0,
  assists: 0,
  yellow_cards: 0,
  red_cards: 0,
  rating: '',
  athlete_notes: '',
  selected: true,
  played: true,
});

/**
 * Phase 4 — Slice C · Unified Create Match Dialog.
 *
 * Step 1: pick Match Source (PFAP | External Club).
 * Step 2: source-aware form.
 *   - PFAP: detailed competition / formation / venue / coach fields.
 *   - External: simpler form keyed off Club + Age Group from Club Mgmt.
 * Step 3: optional bulk add / single add for participants.
 */
const CreateMatchDialog = ({
  open, onOpenChange, athletes, clubs, config, onCreated,
}) => {
  const [step, setStep] = useState(1);
  const [source, setSource] = useState(''); // 'PFAP' | 'External'
  const [form, setForm] = useState({
    club_id: '',
    age_group: '',
    competition: '',
    opponent: '',
    venue: '',
    match_date: '',
    kickoff_time: '',
    formation: '',
    result: '',
    notes: '',
  });
  const [participantMode, setParticipantMode] = useState('bulk'); // 'single' | 'bulk' | 'skip'
  const [bulkAthleteIds, setBulkAthleteIds] = useState([]);
  const [singleParticipant, setSingleParticipant] = useState(blankParticipant());
  const [submitting, setSubmitting] = useState(false);

  const pfapClub = useMemo(() => (clubs || []).find(c => c.is_pfap), [clubs]);
  const externalClubs = useMemo(() => (clubs || []).filter(c => !c.is_pfap), [clubs]);
  const selectedClub = useMemo(
    () => (clubs || []).find(c => c.id === form.club_id),
    [clubs, form.club_id]
  );

  useEffect(() => {
    if (!open) {
      // Reset on close.
      setStep(1); setSource('');
      setForm({
        club_id: '', age_group: '', competition: '', opponent: '', venue: '',
        match_date: '', kickoff_time: '', formation: '', result: '', notes: '',
      });
      setParticipantMode('bulk');
      setBulkAthleteIds([]);
      setSingleParticipant(blankParticipant());
    }
  }, [open]);

  const pickSource = (src) => {
    setSource(src);
    setForm(prev => ({
      ...prev,
      club_id: src === 'PFAP' ? (pfapClub?.id || '') : '',
      age_group: '',
      competition: '',
    }));
    setStep(2);
  };

  const validateForm = () => {
    if (!source) { toast.error('Pick a match source'); return false; }
    if (!form.club_id) { toast.error('Pick a club'); return false; }
    if (!form.opponent.trim()) { toast.error('Opponent is required'); return false; }
    if (!form.match_date) { toast.error('Match date is required'); return false; }
    if (!form.competition.trim()) { toast.error('Competition is required'); return false; }
    if (source === 'External' && !form.age_group) { toast.error('Age group is required for External matches'); return false; }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const payload = {
        match_source: source,
        club_id: form.club_id,
        age_group: form.age_group || null,
        competition: form.competition.trim(),
        opponent: form.opponent.trim(),
        venue: form.venue.trim() || null,
        match_date: form.match_date,
        kickoff_time: form.kickoff_time || null,
        formation: source === 'PFAP' ? (form.formation || null) : null,
        result: form.result.trim() || null,
        notes: form.notes.trim() || null,
      };

      // Build participants array based on mode.
      if (participantMode === 'single' && singleParticipant.athlete_id) {
        payload.participants = [{
          ...singleParticipant,
          rating: singleParticipant.rating ? parseFloat(singleParticipant.rating) : null,
          minutes_played: singleParticipant.minutes_played ? parseInt(singleParticipant.minutes_played, 10) : null,
        }];
      } else if (participantMode === 'bulk' && bulkAthleteIds.length > 0) {
        payload.participants = bulkAthleteIds.map(aid => ({
          athlete_id: aid,
          minutes_played: 90,
          selected: true,
          played: true,
        }));
      }

      const res = await axios.post(`${API}/matches-v3`, payload);
      toast.success(`${source} match created vs ${form.opponent}`);
      onCreated?.(res.data);
      onOpenChange(false);
    } catch (e) {
      // Surface validation arrays (422) clearly instead of "[object Object]".
      const detail = e.response?.data?.detail;
      let msg = 'Failed to create match';
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail
          .slice(0, 3)
          .map(d => `${(d?.loc || []).join('.')} — ${d?.msg || ''}`)
          .join(' · ') || msg;
      }
      console.error('CreateMatchDialog submit failed:', e.response?.status, e.response?.data);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const competitionOptions = source === 'PFAP'
    ? (config?.pfap_competitions || ['Friendly', 'Tournament', 'Internal', 'Showcase'])
    : (config?.external_competitions || ['League', 'Cup', 'Friendly', 'Tournament', 'Other']);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="create-match-v3-dialog">
        <DialogHeader>
          <DialogTitle>
            {step === 1 ? 'Create Match — Step 1: Match Source' : `New ${source} Match`}
          </DialogTitle>
          <DialogDescription>
            {step === 1
              ? 'PFAP for internal & friendlies; External Club for league/cup matches your athletes play for their clubs.'
              : 'Fill in details, then optionally add participating athletes.'}
          </DialogDescription>
        </DialogHeader>

        {/* STEP 1 */}
        {step === 1 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <button
              type="button"
              onClick={() => pickSource('PFAP')}
              className="flex flex-col items-start gap-2 rounded-xl border-2 border-amber-200 hover:border-amber-400 bg-amber-50 dark:bg-amber-950/30 p-5 text-left transition"
              data-testid="pick-source-pfap"
            >
              <div className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-600" />
                <span className="font-semibold text-gray-900 dark:text-gray-100">PFAP Match</span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Friendlies, internal matches, showcases, PFAP tournaments. Full evaluation philosophy supported.
              </p>
            </button>
            <button
              type="button"
              onClick={() => pickSource('External')}
              className="flex flex-col items-start gap-2 rounded-xl border-2 border-sky-200 hover:border-sky-400 bg-sky-50 dark:bg-sky-950/30 p-5 text-left transition"
              data-testid="pick-source-external"
            >
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-sky-600" />
                <span className="font-semibold text-gray-900 dark:text-gray-100">External Club Match</span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                League / cup / friendlies your athletes play for their own clubs. Simpler form.
              </p>
            </button>
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div className="space-y-4 py-2">
            {/* Source-specific top section */}
            {source === 'External' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Club *</Label>
                  <Select value={form.club_id} onValueChange={(v) => setForm({ ...form, club_id: v, age_group: '' })}>
                    <SelectTrigger data-testid="ext-club-trigger">
                      <SelectValue placeholder="Pick a club" />
                    </SelectTrigger>
                    <SelectContent>
                      {externalClubs.length === 0 ? (
                        <SelectItem disabled value="__none__">No external clubs yet — add one in Club Management.</SelectItem>
                      ) : externalClubs.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Age Group *</Label>
                  <Select value={form.age_group} onValueChange={(v) => setForm({ ...form, age_group: v })}>
                    <SelectTrigger data-testid="ext-age-group-trigger" disabled={!selectedClub}>
                      <SelectValue placeholder={selectedClub ? 'Pick age group' : 'Select club first'} />
                    </SelectTrigger>
                    <SelectContent>
                      {(selectedClub?.age_groups || []).map(ag => (
                        <SelectItem key={ag} value={ag}>{ag}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {source === 'PFAP' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Formation</Label>
                  <Input
                    value={form.formation}
                    onChange={e => setForm({ ...form, formation: e.target.value })}
                    placeholder="e.g. 4-3-3"
                    data-testid="pfap-formation-input"
                  />
                </div>
                <div>
                  <Label>Squad / Age Tag</Label>
                  <Select value={form.age_group || ''} onValueChange={(v) => setForm({ ...form, age_group: v })}>
                    <SelectTrigger data-testid="pfap-age-tag-trigger">
                      <SelectValue placeholder="(optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {(pfapClub?.age_groups || []).map(ag => (
                        <SelectItem key={ag} value={ag}>{ag}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Common */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Competition *</Label>
                <Select value={form.competition}
                        onValueChange={(v) => setForm({ ...form, competition: v })}>
                  <SelectTrigger data-testid="competition-trigger">
                    <SelectValue placeholder="Pick competition" />
                  </SelectTrigger>
                  <SelectContent>
                    {competitionOptions.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Opponent *</Label>
                <Input value={form.opponent}
                       onChange={e => setForm({ ...form, opponent: e.target.value })}
                       placeholder="e.g. Oliveirense"
                       data-testid="opponent-input" />
              </div>
              <div>
                <Label>Match Date *</Label>
                <Input type="date"
                       value={form.match_date}
                       onChange={e => setForm({ ...form, match_date: e.target.value })}
                       data-testid="match-date-input" />
              </div>
              <div>
                <Label>Kickoff Time</Label>
                <Input type="time"
                       value={form.kickoff_time}
                       onChange={e => setForm({ ...form, kickoff_time: e.target.value })}
                       data-testid="kickoff-input" />
              </div>
              <div>
                <Label>Venue</Label>
                <Input value={form.venue}
                       onChange={e => setForm({ ...form, venue: e.target.value })}
                       placeholder="Home / Away / stadium"
                       data-testid="venue-input" />
              </div>
              <div>
                <Label>Result</Label>
                <Input value={form.result}
                       onChange={e => setForm({ ...form, result: e.target.value })}
                       placeholder="e.g. 3-1 W (optional)"
                       data-testid="result-input" />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={2}
                        value={form.notes}
                        onChange={e => setForm({ ...form, notes: e.target.value })}
                        data-testid="notes-input" />
            </div>

            {/* Participants */}
            <div className="border-t pt-4">
              <Label className="text-sm font-semibold mb-2 block">Add Participating Athletes (optional)</Label>
              <Tabs value={participantMode} onValueChange={setParticipantMode}>
                <TabsList>
                  <TabsTrigger value="bulk" data-testid="participant-mode-bulk">
                    <UsersIcon className="w-4 h-4 mr-1.5" /> Bulk Lineup
                  </TabsTrigger>
                  <TabsTrigger value="single" data-testid="participant-mode-single">Single Quick-Add</TabsTrigger>
                  <TabsTrigger value="skip" data-testid="participant-mode-skip">Skip For Now</TabsTrigger>
                </TabsList>
                <TabsContent value="bulk" className="space-y-3 mt-3">
                  <p className="text-xs text-gray-500">
                    Each selected athlete gets a participant row with default minutes (90). Edit individual stats from the match detail page after creation.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {(athletes || []).map(a => {
                      const picked = bulkAthleteIds.includes(a.id);
                      return (
                        <button
                          type="button"
                          key={a.id}
                          onClick={() => setBulkAthleteIds(prev =>
                            picked ? prev.filter(id => id !== a.id) : [...prev, a.id]
                          )}
                          className={`px-2.5 py-1 text-xs rounded-md border transition ${
                            picked
                              ? 'bg-emerald-600 text-white border-emerald-600'
                              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-emerald-400'
                          }`}
                          data-testid={`bulk-pick-${a.id}`}
                        >
                          {a.full_name}
                        </button>
                      );
                    })}
                  </div>
                  <div className="text-xs text-gray-500" data-testid="bulk-count">
                    {bulkAthleteIds.length} athlete(s) selected
                  </div>
                </TabsContent>
                <TabsContent value="single" className="space-y-3 mt-3">
                  <div>
                    <Label>Athlete</Label>
                    <SearchableAthleteSelect
                      athletes={athletes || []}
                      value={singleParticipant.athlete_id}
                      onChange={(v) => setSingleParticipant(p => ({ ...p, athlete_id: v }))}
                      placeholder="Pick athlete..."
                      data-testid="single-athlete-select"
                    />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <Label>Minutes</Label>
                      <Input type="number" min="0" max="120"
                             value={singleParticipant.minutes_played}
                             onChange={e => setSingleParticipant(p => ({ ...p, minutes_played: e.target.value }))}
                             data-testid="single-minutes" />
                    </div>
                    <div>
                      <Label>Position</Label>
                      <Input value={singleParticipant.position}
                             onChange={e => setSingleParticipant(p => ({ ...p, position: e.target.value }))}
                             data-testid="single-position" />
                    </div>
                    <div>
                      <Label>Goals</Label>
                      <Input type="number" min="0"
                             value={singleParticipant.goals_scored}
                             onChange={e => setSingleParticipant(p => ({ ...p, goals_scored: parseInt(e.target.value || '0', 10) }))}
                             data-testid="single-goals" />
                    </div>
                    <div>
                      <Label>Assists</Label>
                      <Input type="number" min="0"
                             value={singleParticipant.assists}
                             onChange={e => setSingleParticipant(p => ({ ...p, assists: parseInt(e.target.value || '0', 10) }))}
                             data-testid="single-assists" />
                    </div>
                    <div>
                      <Label>Yellow</Label>
                      <Input type="number" min="0"
                             value={singleParticipant.yellow_cards}
                             onChange={e => setSingleParticipant(p => ({ ...p, yellow_cards: parseInt(e.target.value || '0', 10) }))}
                             data-testid="single-yellow" />
                    </div>
                    <div>
                      <Label>Red</Label>
                      <Input type="number" min="0"
                             value={singleParticipant.red_cards}
                             onChange={e => setSingleParticipant(p => ({ ...p, red_cards: parseInt(e.target.value || '0', 10) }))}
                             data-testid="single-red" />
                    </div>
                    <div>
                      <Label>Rating</Label>
                      <Input type="number" min="1" max="10" step="0.5"
                             value={singleParticipant.rating}
                             onChange={e => setSingleParticipant(p => ({ ...p, rating: e.target.value }))}
                             data-testid="single-rating" />
                    </div>
                  </div>
                  <div>
                    <Label>Notes</Label>
                    <Textarea rows={2}
                              value={singleParticipant.athlete_notes}
                              onChange={e => setSingleParticipant(p => ({ ...p, athlete_notes: e.target.value }))}
                              data-testid="single-notes" />
                  </div>
                </TabsContent>
                <TabsContent value="skip" className="mt-3">
                  <p className="text-xs text-gray-500">
                    You can add participants from the match detail page after creation.
                  </p>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 2 && (
            <Button variant="ghost" onClick={() => setStep(1)} data-testid="back-to-source">
              ← Back
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          {step === 2 && (
            <Button onClick={handleSubmit} disabled={submitting} data-testid="submit-create-match">
              {submitting ? 'Creating...' : 'Create Match'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateMatchDialog;
